/**
 * The audio subsystem (U17, R26).
 *
 * Listens to the simulation's event vocabulary and synthesizes a response.
 * **Audio never appears in the simulation path** — `src/sim/` does not reference
 * this subsystem at all, and a test greps for that, because the headless runner
 * has no `AudioContext` and R3 requires the simulation to stay DOM-free.
 *
 * A voice budget caps concurrent sources and drops the quietest. Four hundred
 * units firing will otherwise clip the master bus and stall the audio thread,
 * which is a far worse failure than a missing gunshot.
 */

import type { Ctx, Subsystem } from '../core/registry.js'
import { VOICE_BUDGET } from '../core/config.js'
import { Synth } from './synth.js'
import { SFX, playSfx, type SfxName } from './sfx.js'
import { Announcer, VOX } from './announcer.js'
import { Spatial } from './spatial.js'
import { Music } from './music.js'
import { defById } from '../sim/data/entities.js'
import { weaponById, PROJECTILE } from '../sim/data/weapons.js'
import { WARHEAD } from '../sim/data/armor.js'
import * as fix from '../core/fixed.js'

interface ActiveVoice {
  name: SfxName
  endsAt: number
  gain: GainNode
  panner: PannerNode | null
  priority: number
}

export class Audio implements Subsystem {
  static id = 'audio'
  static deps = ['entities'] as const

  private ctx!: Ctx
  private audio: BaseAudioContext | null = null
  private master: GainNode | null = null
  private synth: Synth | null = null
  private spatial: Spatial | null = null
  private announcer: Announcer | null = null

  private readonly active: ActiveVoice[] = []
  /** Local player slot, so only your own events get an announcer line. */
  localSlot = 0
  private enabled = true

  /** The score. Composed rather than synthesised — see `music.ts`. */
  readonly music = new Music()
  /** Ticks since anything last fired, so the score knows if there is a battle. */
  private combatTick = -1

  /** Voices started since boot. The leak test watches this against node count. */
  voicesStarted = 0
  voicesDropped = 0

  constructor(private readonly context?: BaseAudioContext) {}

  init(ctx: Ctx): void {
    this.ctx = ctx
    try {
      this.audio =
        this.context ??
        (typeof AudioContext !== 'undefined' ? new AudioContext({ latencyHint: 'interactive' }) : null)
    } catch {
      this.audio = null
    }
    if (!this.audio) {
      // No Web Audio: the game plays in silence rather than failing to boot.
      this.enabled = false
      return
    }

    this.master = this.audio.createGain()
    this.master.gain.value = 0.55
    this.master.connect(this.audio.destination)

    this.music.load()
    this.synth = new Synth(this.audio)
    this.spatial = new Spatial(this.audio, this.master)
    this.announcer = new Announcer(this.synth, this.master, () => this.audio!.currentTime)

    this.subscribe(ctx)
  }

  private subscribe(ctx: Ctx): void {
    ctx.on('weapon:fire', (p) => {
      this.combatTick = this.ctx.time.tick
      this.onWeaponFire(p as { weapon: number; x: number; y: number })
    })
    ctx.on('weapon:impact', (p) => {
      const q = p as { weapon: number; x: number; y: number }
      const weapon = weaponById(q.weapon)
      const big = weapon && weapon.splash > 0
      this.play(big ? SFX.ExplosionSmall : SFX.Impact, q.x, q.y, big ? 2 : 0)
    })

    ctx.on('actor:death', (p) => {
      const q = p as { kind: number; owner: number; x: number; y: number }
      const def = defById(q.kind)
      if (def?.isStructure) {
        this.play(SFX.StructureCollapse, q.x, q.y, 4)
        if (q.owner === this.localSlot) this.announcer?.say(VOX.StructureLost, true)
      } else {
        this.play(SFX.ExplosionSmall, q.x, q.y, 1)
        if (q.owner === this.localSlot) this.announcer?.say(VOX.UnitLost)
      }
    })

    ctx.on('actor:damaged', (p) => {
      const q = p as { id: number }
      void q
    })

    ctx.on('build:queued', (p) => {
      // Filtered like everything else here: the simulation emits this for every
      // player, so an unfiltered handler clicks every time the AI queues a unit.
      if ((p as { owner: number }).owner !== this.localSlot) return
      this.play(SFX.BuildStart)
    })
    ctx.on('build:complete', (p) => {
      const q = p as { owner: number; kind: number }
      if (q.owner !== this.localSlot) return
      this.play(SFX.BuildComplete)
      const def = defById(q.kind)
      this.announcer?.say(def?.isStructure ? VOX.ConstructionComplete : VOX.UnitReady)
    })
    ctx.on('struct:placed', (p) => {
      // Positional, so an enemy placing a building far away is inaudible on its
      // own merits — but it should still not sound like it happened here.
      const q = p as { owner?: number; x: number; y: number }
      if (q.owner !== undefined && q.owner !== this.localSlot) return
      this.play(SFX.Place, fix.fromInt(q.x), fix.fromInt(q.y), 2)
    })

    ctx.on('order:rejected', (p) => {
      const q = p as { owner?: number; reason: string }
      if (q.owner !== undefined && q.owner !== this.localSlot) return
      this.play(SFX.Reject, undefined, undefined, 3)
      if (q.reason === 'insufficient-credits') this.announcer?.say(VOX.InsufficientFunds)
    })
    ctx.on('order:acknowledged', () => this.play(SFX.OrderAck))
    ctx.on('selection:changed', (p) => {
      const q = p as { ids: number[] }
      if (q.ids.length > 0) this.play(SFX.Select)
    })

    ctx.on('harvest:unload', (p) => {
      const q = p as { owner: number }
      if (q.owner === this.localSlot) this.play(SFX.HarvestUnload)
    })
    ctx.on('econ:power', (p) => {
      const q = p as { owner: number; factor: number }
      if (q.owner !== this.localSlot) return
      if (q.factor < fix.ONE) {
        this.play(SFX.PowerLow, undefined, undefined, 3)
        this.announcer?.say(VOX.LowPower)
      }
    })

    ctx.on('super:ready', (p) => {
      if ((p as { owner: number }).owner !== this.localSlot) return
      this.play(SFX.SuperReady, undefined, undefined, 5)
      this.announcer?.say(VOX.SuperweaponReady, true)
    })
    ctx.on('super:fire', (p) => {
      const q = p as { x: number; y: number }
      this.play(SFX.SuperFire, fix.fromInt(q.x), fix.fromInt(q.y), 6)
    })

    ctx.on('match:over', (p) => {
      const q = p as { winner: number }
      this.announcer?.say(q.winner === this.localSlot ? VOX.MissionAccomplished : VOX.MissionFailed, true)
    })
  }

  private onWeaponFire(p: { weapon: number; x: number; y: number }): void {
    const weapon = weaponById(p.weapon)
    if (!weapon) return
    let name: SfxName = SFX.WeaponSmallArms
    if (weapon.warhead === WARHEAD.ApShell) name = SFX.WeaponCannon
    else if (weapon.warhead === WARHEAD.Arc) name = SFX.WeaponArc
    else if (weapon.warhead === WARHEAD.Siege || weapon.projectile === PROJECTILE.Ballistic) {
      name = SFX.WeaponMortar
    }
    this.play(name, p.x, p.y, 1)
  }

  /**
   * Play an effect, spatialized if a world position is given.
   *
   * When the voice budget is full the **quietest** active voice is dropped
   * rather than the newest — a distant gunshot should never silence the
   * explosion in front of you.
   */
  play(name: SfxName, worldX?: number, worldY?: number, priority = 0): void {
    if (!this.enabled || !this.synth || !this.master || !this.audio) return

    if (this.active.length >= VOICE_BUDGET) {
      if (!this.dropQuietest(priority)) {
        this.voicesDropped++
        return
      }
    }

    const gain = this.audio.createGain()
    let panner: PannerNode | null = null
    let destination: AudioNode = this.master

    if (worldX !== undefined && worldY !== undefined && this.spatial) {
      const placed = this.spatial.place(fix.toFloat(fix.raw(worldX)), fix.toFloat(fix.raw(worldY)))
      panner = placed.panner
      destination = placed.input
      gain.connect(panner)
    } else {
      gain.connect(this.master)
    }

    const endsAt = playSfx(this.synth, name, worldX !== undefined ? destination : gain, this.audio.currentTime)
    if (worldX === undefined) {
      // Non-spatial voices route through the gain node directly.
      gain.gain.value = 1
    }

    const voice: ActiveVoice = { name, endsAt, gain, panner, priority }
    this.active.push(voice)
    this.voicesStarted++
  }

  /** Drop the lowest-priority active voice if it is quieter than the newcomer. */
  private dropQuietest(priority: number): boolean {
    let worst = -1
    let worstPriority = priority
    for (let i = 0; i < this.active.length; i++) {
      if (this.active[i].priority < worstPriority) {
        worstPriority = this.active[i].priority
        worst = i
      }
    }
    if (worst < 0) return false
    this.release(this.active[worst])
    this.active.splice(worst, 1)
    return true
  }

  /** Reclaim finished voices. Called every render frame. */
  update(): void {
    this.music.update(this.ctx.time.tick - this.combatTick < 30)
    if (!this.audio) return
    const now = this.audio.currentTime
    for (let i = this.active.length - 1; i >= 0; i--) {
      if (this.active[i].endsAt > now) continue
      this.release(this.active[i])
      this.active.splice(i, 1)
    }
  }

  private release(voice: ActiveVoice): void {
    try {
      voice.gain.disconnect()
      voice.panner?.disconnect()
    } catch {
      // Already torn down by the source's onended handler.
    }
  }

  /** Update the listener position as the camera pans. */
  setListener(worldX: number, worldZ: number): void {
    this.spatial?.setListener(worldX, worldZ)
  }

  get activeVoices(): number {
    return this.active.length
  }

  get announcerAvailable(): boolean {
    return this.announcer?.isAvailable ?? false
  }

  get spokenLines(): readonly string[] {
    return this.announcer?.spoken ?? []
  }

  /**
   * Resume the context. Browsers require a user gesture before audio plays,
   * so the composition root calls this on the first click.
   */
  resume(): void {
    const ctx = this.audio as AudioContext | null
    if (ctx && typeof ctx.resume === 'function' && ctx.state === 'suspended') void ctx.resume()
    // The same gesture that unlocks the audio context starts the score.
    this.music.start()
  }

  dispose(): void {
    this.music.dispose()
    for (const voice of this.active) this.release(voice)
    this.active.length = 0
    this.announcer?.cancel()
    this.master?.disconnect()
    const ctx = this.audio as AudioContext | null
    if (ctx && typeof ctx.close === 'function') void ctx.close()
    void this.ctx
  }
}

export { SFX, VOX }
